import { SkyButton, SkyInput, SkyText } from "@skypointcloud/skypoint-ui";
import { useEffect, useRef, useState } from "react";
import { isRealDate } from "@shared/cron/cronUtils";
import { MAX_SKIP_DATES } from "@shared/types/schedules";
import { AddIcon, DismissIcon } from "@/lib/icons";
import { formatSkipDate, todayInZone, type ScheduleDraft } from "./scheduleForm";
import { byCodeUnit } from "@shared/sort";

/**
 * "Skip these dates" on the When card (Session 10, P9, decision B7).
 *
 * Whole local days, in the schedule's own zone, on which it does not fire: a holiday, a
 * maintenance window. Each added day becomes a chip with its own Remove button, and the next run
 * beside Save already accounts for them (the same shared day walk the runner uses).
 *
 * The control is a date field with an explicit "Add date" button, not `SkyDatePicker`: in
 * skypoint-ui 2.4.0 the picker wraps its input in a Radix popover trigger rendered as
 * `<div type="button" aria-haspopup="dialog">`, which axe reports as `aria-allowed-attr`
 * (critical), the same defect `SkyTimePicker` had (2.17.0 fixes both; the When card's times use
 * the time picker since 2026-09-28, and this field's switch is a backlog row). The button
 * matters as much: a date field reports a complete date after every keystroke of its day and year
 * segments, so adding on change would add the 2nd before the user typed the 5 of the 25th.
 *
 * The server drops duplicates and days already past on every save, and refuses more than
 * `MAX_SKIP_DATES`; the field refuses a past day and stops at the limit.
 */

export interface ScheduleSkipDatesProps {
  draft: ScheduleDraft;
  onChange: (draft: ScheduleDraft) => void;
  disabled?: boolean;
  /** What the server said about `skipDates`, when it refused them. */
  error?: string;
}

export function ScheduleSkipDates({
  draft,
  onChange,
  disabled = false,
  error,
}: ScheduleSkipDatesProps) {
  const [value, setValue] = useState("");
  const fieldRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  /** Where focus goes after the rows re-render: a chip index to land near, or the field. */
  const focusNext = useRef<number | "field" | null>(null);

  useEffect(() => {
    const next = focusNext.current;
    if (next === null) return;
    focusNext.current = null;
    const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>("button");
    const target =
      next === "field" || !buttons || buttons.length === 0
        ? fieldRef.current?.querySelector<HTMLInputElement>("input")
        : buttons[Math.min(next, buttons.length - 1)];
    target?.focus();
  });

  const dates = draft.skipDates;
  const atLimit = dates.length >= MAX_SKIP_DATES;
  // The schedule's today, as the server prunes against it, not the browser's.
  const today = todayInZone(draft.timezone);
  const chosen = isRealDate(value) ? value : null;
  const past = chosen !== null && chosen < today;
  const already = chosen !== null && dates.includes(chosen);
  const canAdd = chosen !== null && !past && !already && !atLimit && !disabled;

  const add = (): void => {
    if (!canAdd || !chosen) return;
    focusNext.current = "field";
    setValue("");
    onChange({ ...draft, skipDates: [...dates, chosen].sort(byCodeUnit) });
  };

  const remove = (index: number): void => {
    focusNext.current = index;
    onChange({ ...draft, skipDates: dates.filter((_, i) => i !== index) });
  };

  const fieldMessageOf = (): string | undefined => {
    if (error) return error;
    if (past) return "Choose today or a later day.";
    if (already) return `${formatSkipDate(chosen as string)} is already skipped.`;
    return undefined;
  };
  const fieldMessage = fieldMessageOf();

  return (
    <div className="flex flex-col gap-2" data-testid="schedule-skip-dates">
      <div className="flex flex-wrap items-end gap-2">
        <div
          ref={fieldRef}
          className="w-56"
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
        >
          <SkyInput
            type="date"
            label="Skip these dates"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            disabled={disabled || atLimit}
            hint={
              atLimit
                ? `A schedule can skip at most ${MAX_SKIP_DATES} dates. Remove one to add another.`
                : "Whole days in this schedule's time zone. No import starts on them."
            }
            validationState={fieldMessage ? "error" : "none"}
            validationMessage={fieldMessage}
            testId="schedule-skip-date-input"
          />
        </div>
        <SkyButton
          appearance="secondary"
          icon={AddIcon}
          disabled={!canAdd}
          onClick={add}
          testId="schedule-skip-date-add"
        >
          Add date
        </SkyButton>
      </div>
      {dates.length === 0 ? (
        <SkyText size={200} color="muted" testId="schedule-skip-dates-empty">
          No dates are skipped.
        </SkyText>
      ) : (
        <ul
          ref={listRef}
          aria-label="Skipped dates"
          className="flex flex-wrap gap-2"
          data-testid="schedule-skip-date-list"
        >
          {dates.map((day, index) => (
            <li
              key={day}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-muted py-0.5 pl-3 pr-1"
              data-testid={`schedule-skip-date-${day}`}
            >
              <SkyText size={200}>
                <time dateTime={day}>{formatSkipDate(day)}</time>
              </SkyText>
              <SkyButton
                appearance="subtle"
                size="small"
                icon={DismissIcon}
                aria-label={`Remove ${formatSkipDate(day)}`}
                disabled={disabled}
                onClick={() => remove(index)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
